import React, { useState, useMemo } from 'react';
import { Col, Row, getItemWithExpiration } from '../../lib/utils';
import { colors } from '../../lib/colors';
import { Subtitle1Bold, Body2Regular, CaptionLight } from '../../lib/font';
import ColorSquareButton from '../../components/Button/ColorSquareButton';
import GroupSessionDetailPopover from '../../components/GroupSession/GroupSessionDetailPopover';
import QuickGroupSessionModal from '../../components/GroupSession/QuickGroupSessionModal';
import GroupSessionCreateModal from '../../components/GroupSession/GroupSessionCreateModal';
import { useGroupSessions } from '@/features/groupSession/hooks';
import { GroupSession, ClassType } from '@/features/groupSession/types';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';

dayjs.locale('ko');

const CLASS_TYPE_COLORS: Record<string, { bg: string; border: string; text: string; label: string }> = {
    GROUP: { bg: '#ECFDF5', border: '#10B981', text: '#065F46', label: '그룹' },
    PRIVATE_1ON2: { bg: '#EFF6FF', border: '#3B82F6', text: '#1E40AF', label: '1:2' },
    PRIVATE_1ON3: { bg: '#F5F3FF', border: '#8B5CF6', text: '#5B21B6', label: '1:3' },
    PRIVATE_1ON1: { bg: '#FEF3C7', border: '#F59E0B', text: '#92400E', label: '1:1' },
};

const STATUS_COLORS: Record<string, { bg: string; text: string; label: string }> = {
    ACTIVE: { bg: '#ECFDF5', text: '#065F46', label: '예정' },
    CANCELLED: { bg: '#FEF2F2', text: '#991B1B', label: '취소' },
};

const HOURS = Array.from({ length: 16 }, (_, i) => i + 6); // 06:00 ~ 21:00

const GroupSchedulePage = ({ createAlert }: { createAlert: any }) => {
    const [authObject] = useState(getItemWithExpiration('authObject'));
    const centerId = authObject?.centerId;

    const [weekOffset, setWeekOffset] = useState(0);
    const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
    const [showQuickCreate, setShowQuickCreate] = useState(false);
    const [quickCreateDate, setQuickCreateDate] = useState<string | null>(null);
    const [filterClassType, setFilterClassType] = useState<ClassType | 'ALL'>('ALL');
    const [showFullCreate, setShowFullCreate] = useState(false);

    // 주간 날짜 계산
    const weekStart = useMemo(() => dayjs().startOf('week').add(weekOffset * 7, 'day'), [weekOffset]);
    const weekEnd = useMemo(() => weekStart.endOf('week'), [weekStart]);
    const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => weekStart.add(i, 'day')), [weekStart]);

    const { data: sessions = [], isLoading } = useGroupSessions({
        centerId,
        startDate: weekStart.toISOString(),
        endDate: weekEnd.toISOString(),
        classType: filterClassType === 'ALL' ? undefined : filterClassType,
    });

    // 요일별 세션 그룹핑
    const sessionsByDay = useMemo(() => {
        const map = new Map<string, GroupSession[]>();
        weekDays.forEach((d) => map.set(d.format('YYYY-MM-DD'), []));
        (sessions as GroupSession[]).forEach((s) => {
            if (!s.startTime) return;
            const parsed = dayjs(s.startTime);
            if (!parsed.isValid()) return;
            const key = parsed.format('YYYY-MM-DD');
            const arr = map.get(key);
            if (arr) arr.push(s);
        });
        return map;
    }, [sessions, weekDays]);

    const isToday = (d: dayjs.Dayjs) => d.isSame(dayjs(), 'day');

    const handleDayClick = (date: dayjs.Dayjs) => {
        setQuickCreateDate(date.format('YYYY-MM-DD'));
        setShowQuickCreate(true);
    };

    return (
        <Col style={{ width: '100%', height: '100%', backgroundColor: '#F8FAFC', marginTop: 64, overflow: 'auto' }}>
            <Col style={{ width: '100%', maxWidth: 1200, margin: '0 auto', padding: '32px 24px 80px' }}>
                {/* 헤더 */}
                <Row style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                    <Col>
                        <Subtitle1Bold>수업 스케줄</Subtitle1Bold>
                        <CaptionLight color={colors.LAVEL_1} style={{ marginTop: 4 }}>
                            주간 그룹 수업 일정을 확인하고 관리합니다
                        </CaptionLight>
                    </Col>
                    <ColorSquareButton text="+ 빠른 수업 생성" size="small" callBack={() => setShowQuickCreate(true)} />
                </Row>

                {/* 주간 네비게이션 */}
                <Row style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                    <Row style={{ alignItems: 'center', gap: 12 }}>
                        <button onClick={() => setWeekOffset(weekOffset - 1)} style={navBtnStyle}>{'<'}</button>
                        <span style={{ fontSize: 18, fontWeight: 700, color: '#111827', minWidth: 200, textAlign: 'center' }}>
                            {weekStart.format('YYYY.MM.DD')} - {weekEnd.format('MM.DD')}
                        </span>
                        <button onClick={() => setWeekOffset(weekOffset + 1)} style={navBtnStyle}>{'>'}</button>
                        {weekOffset !== 0 && (
                            <button onClick={() => setWeekOffset(0)}
                                style={{ fontSize: 13, fontWeight: 600, color: '#3B82F6', background: 'none', border: 'none', cursor: 'pointer', marginLeft: 4 }}>
                                이번 주
                            </button>
                        )}
                    </Row>

                    {/* 필터 */}
                    <Row style={{ gap: 6 }}>
                        {(['ALL', 'GROUP', 'PRIVATE_1ON2', 'PRIVATE_1ON3'] as const).map((type) => {
                            const active = filterClassType === type;
                            const cfg = type === 'ALL' ? null : CLASS_TYPE_COLORS[type];
                            return (
                                <button key={type} onClick={() => setFilterClassType(type)}
                                    style={{
                                        padding: '6px 14px', borderRadius: 20, fontSize: 13, fontWeight: active ? 600 : 400,
                                        border: active ? `1.5px solid ${cfg?.border || '#374151'}` : '1px solid #E1E4E6',
                                        backgroundColor: active ? (cfg?.bg || '#F3F4F6') : 'white',
                                        color: active ? (cfg?.text || '#374151') : '#9CA3AF',
                                        cursor: 'pointer',
                                    }}>
                                    {type === 'ALL' ? '전체' : cfg?.label}
                                </button>
                            );
                        })}
                    </Row>
                </Row>

                {/* 주간 캘린더 그리드 */}
                <div style={{ border: '1px solid #E5E7EB', borderRadius: 12, overflow: 'hidden', backgroundColor: 'white' }}>
                    {/* 요일 헤더 */}
                    <div style={{ display: 'grid', gridTemplateColumns: '60px repeat(7, 1fr)', borderBottom: '1px solid #E5E7EB' }}>
                        <div style={{ padding: '12px 0', borderRight: '1px solid #F3F4F6' }} />
                        {weekDays.map((d) => (
                            <div key={d.format('YYYY-MM-DD')}
                                style={{
                                    padding: '12px 8px', textAlign: 'center', borderRight: '1px solid #F3F4F6',
                                    backgroundColor: isToday(d) ? '#EFF6FF' : undefined,
                                }}>
                                <span style={{
                                    fontSize: 12, color: isToday(d) ? '#2563EB' : d.day() === 0 ? '#EF4444' : d.day() === 6 ? '#3B82F6' : '#6B7280',
                                    fontWeight: 500,
                                }}>
                                    {d.format('ddd')}
                                </span>
                                <div style={{
                                    fontSize: 18, fontWeight: isToday(d) ? 700 : 500,
                                    color: isToday(d) ? '#2563EB' : '#374151',
                                    marginTop: 2,
                                }}>
                                    {d.format('D')}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* 시간 그리드 */}
                    {isLoading ? (
                        <div style={{ padding: '60px 0', textAlign: 'center', color: '#9CA3AF', fontSize: 14 }}>불러오는 중...</div>
                    ) : (
                        <div style={{ position: 'relative' }}>
                            {HOURS.map((hour) => (
                                <div key={hour} style={{ display: 'grid', gridTemplateColumns: '60px repeat(7, 1fr)', minHeight: 60, borderBottom: '1px solid #F3F4F6' }}>
                                    <div style={{ padding: '4px 8px', textAlign: 'right', fontSize: 11, color: '#9CA3AF', borderRight: '1px solid #F3F4F6' }}>
                                        {String(hour).padStart(2, '0')}:00
                                    </div>
                                    {weekDays.map((d) => {
                                        const dayKey = d.format('YYYY-MM-DD');
                                        const daySessions = (sessionsByDay.get(dayKey) || []).filter((s) => {
                                            const h = dayjs(s.startTime).hour();
                                            return h === hour;
                                        });
                                        return (
                                            <div key={dayKey + hour}
                                                onClick={() => handleDayClick(d)}
                                                style={{
                                                    borderRight: '1px solid #F3F4F6', padding: '2px 4px',
                                                    cursor: 'pointer', position: 'relative', minHeight: 60,
                                                    backgroundColor: isToday(d) ? '#FAFCFF' : undefined,
                                                }}>
                                                {daySessions.map((s) => {
                                                    const cfg = CLASS_TYPE_COLORS[s.classType] || CLASS_TYPE_COLORS.GROUP;
                                                    const statusCfg = STATUS_COLORS[s.status] || STATUS_COLORS.ACTIVE;
                                                    return (
                                                        <div key={s._id}
                                                            onClick={(e) => { e.stopPropagation(); setSelectedSessionId(s._id); }}
                                                            style={{
                                                                backgroundColor: s.status === 'CANCELLED' ? '#FEF2F2' : cfg.bg,
                                                                borderLeft: `3px solid ${s.status === 'CANCELLED' ? '#EF4444' : cfg.border}`,
                                                                borderRadius: 6, padding: '6px 8px', marginBottom: 4,
                                                                cursor: 'pointer', transition: 'transform 0.1s',
                                                                opacity: s.status === 'CANCELLED' ? 0.6 : 1,
                                                            }}
                                                            onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.02)')}
                                                            onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}>
                                                            <Row style={{ alignItems: 'center', gap: 4, marginBottom: 2 }}>
                                                                <span style={{ fontSize: 11, fontWeight: 600, color: cfg.text, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                    {s.title}
                                                                </span>
                                                                <span style={{
                                                                    fontSize: 9, fontWeight: 600, padding: '1px 4px', borderRadius: 3,
                                                                    backgroundColor: cfg.border, color: 'white',
                                                                }}>
                                                                    {cfg.label}
                                                                </span>
                                                            </Row>
                                                            <span style={{ fontSize: 10, color: '#6B7280' }}>
                                                                {dayjs(s.startTime).format('HH:mm')}~{dayjs(s.endTime).format('HH:mm')}
                                                            </span>
                                                            <Row style={{ alignItems: 'center', gap: 4, marginTop: 2 }}>
                                                                {s.coachName ? (
                                                                    <span style={{ fontSize: 10, color: '#6B7280' }}>{s.coachName}</span>
                                                                ) : (
                                                                    <span style={{ fontSize: 9, color: '#F59E0B', fontWeight: 600, padding: '1px 4px', borderRadius: 3, backgroundColor: '#FFFBEB' }}>추후 배정 예정</span>
                                                                )}
                                                                <span style={{ fontSize: 10, color: s.currentCount >= s.maxCapacity ? '#EF4444' : '#6B7280', marginLeft: 'auto' }}>
                                                                    {s.currentCount}/{s.maxCapacity}명
                                                                </span>
                                                            </Row>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        );
                                    })}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* 범례 */}
                <Row style={{ gap: 16, marginTop: 16, justifyContent: 'center' }}>
                    {Object.entries(CLASS_TYPE_COLORS).map(([key, cfg]) => (
                        <Row key={key} style={{ alignItems: 'center', gap: 6 }}>
                            <div style={{ width: 12, height: 12, borderRadius: 3, backgroundColor: cfg.border }} />
                            <span style={{ fontSize: 12, color: '#6B7280' }}>{cfg.label}</span>
                        </Row>
                    ))}
                    <Row style={{ alignItems: 'center', gap: 6 }}>
                        <div style={{ width: 12, height: 12, borderRadius: 3, backgroundColor: '#EF4444', opacity: 0.6 }} />
                        <span style={{ fontSize: 12, color: '#6B7280' }}>취소됨</span>
                    </Row>
                </Row>
            </Col>

            {/* 세션 상세 팝오버 */}
            {selectedSessionId && (
                <GroupSessionDetailPopover
                    sessionId={selectedSessionId}
                    onClose={() => setSelectedSessionId(null)}
                    createAlert={createAlert}
                />
            )}

            {/* 빠른 수업 생성 모달 */}
            {showQuickCreate && (
                <QuickGroupSessionModal
                    onClose={() => { setShowQuickCreate(false); setQuickCreateDate(null); }}
                    onOpenFullCreate={() => { setShowQuickCreate(false); setShowFullCreate(true); }}
                    createAlert={createAlert}
                    defaultDate={quickCreateDate || undefined}
                />
            )}

            {/* 전체 수업 생성 모달 */}
            {showFullCreate && (
                <GroupSessionCreateModal
                    onClose={() => setShowFullCreate(false)}
                    createAlert={createAlert}
                />
            )}
        </Col>
    );
};

const navBtnStyle: React.CSSProperties = {
    width: 36, height: 36, borderRadius: 8, border: '1px solid #E1E4E6',
    backgroundColor: 'white', fontSize: 16, fontWeight: 600, color: '#374151',
    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
};

export default GroupSchedulePage;
